
'use client';

import React, { useState, useEffect } from 'react';
import { 
  getCurrentStoreId, 
  setCurrentStoreId, 
  AVAILABLE_STORES, 
  getCurrentStoreInfo,
  onStoreIdChange,
  isDevelopment,
  clearStoreId,
  type StoreInfo
} from '../utils/storeIdManager';

/**
 * Development Store Selector Component
 * 
 * This component allows developers to switch between different stores
 * during development. It's only visible in development environment.
 */

interface DevStoreSelectorProps {
  className?: string;
  position?: 'fixed' | 'relative';
  showLabel?: boolean;
}

const DevStoreSelector: React.FC<DevStoreSelectorProps> = ({ 
  className = '', 
  position = 'fixed',
  showLabel = true 
}) => {
  const [currentStore, setCurrentStore] = useState<StoreInfo | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    setIsVisible(isDevelopment());
    
    if (isDevelopment()) {
      setCurrentStore(getCurrentStoreInfo());

      // Listen for store changes
      const unsubscribe = onStoreIdChange((storeId, storeName) => {
        setCurrentStore(getCurrentStoreInfo());
        console.log(`🏪 Store switched to: ${storeName} (${storeId})`);
      });

      return unsubscribe;
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  const handleStoreChange = (storeId: string) => {
    setCurrentStoreId(storeId);
    setIsOpen(false);
  };

  const handleReset = () => {
    clearStoreId();
    setIsOpen(false);
  };

  const baseClasses = position === 'fixed' 
    ? 'fixed bottom-4 right-4 z-50' 
    : 'relative';

  return (
    <div className={`${baseClasses} ${className}`}>
      <div className="bg-blue-600 text-white rounded-lg shadow-lg border border-blue-700">
        {/* Header/Toggle Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 text-left font-medium text-sm flex items-center justify-between hover:bg-blue-700 rounded-lg transition-colors"
          title="Development Store Selector"
        >
          <div className="flex items-center gap-2">
            <span className="text-xs">🏪</span>
            {showLabel && (
              <span className="hidden sm:inline">
                DEV: {currentStore?.name || 'Unknown'}
              </span>
            )}
          </div>
          <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute bottom-full right-0 mb-2 bg-white text-gray-800 rounded-lg shadow-xl border border-gray-200 min-w-48 max-h-64 overflow-y-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Development Stores
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Current: {currentStore?.id}
              </div>
            </div>
            
            <div className="py-1">
              {AVAILABLE_STORES.map((store) => (
                <button
                  key={store.id}
                  onClick={() => handleStoreChange(store.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${
                    currentStore?.id === store.id 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-700'
                  }`}
                  title={store.description}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{store.name}</div>
                      <div className="text-xs text-gray-500">{store.id}</div>
                    </div>
                    {currentStore?.id === store.id && (
                      <span className="text-blue-500 text-xs">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-200 p-2">
              <button
                onClick={handleReset}
                className="w-full text-left px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Reset to default store"
              >
                🔄 Reset to Default
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Store Info Badge (when closed) */}
      {!isOpen && currentStore && (
        <div className="mt-1 text-xs text-center text-blue-200">
          {currentStore.id}
        </div>
      )}
    </div>
  );
};

export default DevStoreSelector;
